import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Product } from '../services/types';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

interface WishlistContextType {
  wishlist: Product[];
  addToWishlist: (product: Product) => Promise<void>;
  removeFromWishlist: (productId: string | number) => Promise<void>;
  isInWishlist: (productId: string | number) => boolean;
  toggleWishlist: (product: Product) => Promise<void>;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch wishlist from Supabase or LocalStorage
  useEffect(() => {
    const fetchWishlist = async () => {
      if (user) {
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('wishlist_items')
            .select(`
              product_id,
              product:products (
                id,
                name,
                category,
                price,
                unit,
                stock,
                description,
                image,
                rating,
                shop_id,
                seller_id
              )
            `)
            .eq('user_id', user.id);

          if (error) throw error;

          if (data) {
            const mappedProducts: Product[] = data.map((item: any) => {
              const p = item.product;
              if (!p) return null;
              
              return {
                id: p.id,
                name: p.name,
                category: p.category,
                price: p.price,
                unit: p.unit,
                stock: p.stock,
                description: p.description,
                image: p.image || '',
                features: (p as any).features || [],
                tags: (p as any).tags || [],
                rating: p.rating || 4.5,
                reviews: 0,
                seller: {
                   id: p.seller_id,
                   name: 'Shop Seller',
                   location: 'Kenya',
                   rating: 4.5,
                   verified: true,
                   joinedDate: '2023-01-01',
                   responseRate: '95%'
                }
              } as any;
            }).filter(Boolean);
            setWishlist(mappedProducts);
          }
        } catch (error) {
          console.error('Error fetching wishlist:', error);
        } finally {
          setLoading(false);
        }
      } else {
        // Fallback to local storage for guest
        const savedWishlist = localStorage.getItem('farmers_hub_wishlist');
        if (savedWishlist) {
          try {
            setWishlist(JSON.parse(savedWishlist));
          } catch (e) {
            console.error('Failed to parse wishlist', e);
          }
        }
      }
    };

    fetchWishlist();
  }, [user]);

  // Save to local storage only if not logged in
  useEffect(() => {
    if (!user) {
      localStorage.setItem('farmers_hub_wishlist', JSON.stringify(wishlist));
    }
  }, [wishlist, user]);

  const addToWishlist = async (product: Product) => {
    if (isInWishlist(product.id)) return;

    // Optimistic update
    setWishlist((prev) => [...prev, product]);

    if (user) {
      try {
        const { error } = await supabase
          .from('wishlist_items')
          .insert({ user_id: user.id, product_id: product.id });
        
        if (error) {
          console.error('Error adding to wishlist DB:', error);
          // Revert on error
          setWishlist((prev) => prev.filter(p => p.id !== product.id));
        }
      } catch (err) {
        console.error('Error adding to wishlist:', err);
        setWishlist((prev) => prev.filter(p => p.id !== product.id));
      }
    }
  };

  const removeFromWishlist = async (productId: string | number) => {
    // Optimistic update
    const previousWishlist = [...wishlist];
    setWishlist((prev) => prev.filter((p) => p.id.toString() !== productId.toString()));

    if (user) {
      try {
        const { error } = await supabase
          .from('wishlist_items')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId);
          
        if (error) {
          console.error('Error removing from wishlist DB:', error);
          // Revert
          setWishlist(previousWishlist);
        }
      } catch (err) {
        console.error('Error removing from wishlist:', err);
        setWishlist(previousWishlist);
      }
    }
  };

  const isInWishlist = (productId: string | number) => {
    return wishlist.some((p) => p.id.toString() === productId.toString());
  };

  const toggleWishlist = async (product: Product) => {
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id);
    } else {
      await addToWishlist(product);
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        toggleWishlist,
        loading
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
